
import React, { useState } from 'react';

export const FAQSection: React.FC = () => {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const faqs = [
    {
      question: "How quickly can you buy my multifamily property?",
      answer: "We can close on your multifamily property in as little as 14 days. Our cash purchasing power means no waiting for buyer financing approvals or lengthy underwriting processes."
    },
    {
      question: "Do you buy properties in any condition?",
      answer: "Yes, we buy multifamily properties and commercial buildings in any condition. Whether your property needs major repairs, has vacancy issues, or is in excellent condition, we'll make you a fair cash offer."
    },
    {
      question: "What types of multifamily properties do you purchase?",
      answer: "We purchase all types of multifamily properties including garden apartments, mid-rise buildings, high-rise towers, mixed-use properties, student housing, senior communities, and commercial buildings with residential units."
    },
    {
      question: "How do you determine the cash offer amount?",
      answer: "Our experienced team evaluates your property based on current market conditions, property condition, rental income, location, and comparable sales. We provide fair market value offers that reflect the true worth of your investment."
    },
    {
      question: "Are there any fees or commissions?",
      answer: "No, there are absolutely no fees, commissions, or hidden costs. The cash offer we present is exactly what you'll receive at closing. We handle all closing costs and paperwork."
    },
    {
      question: "Do I need to make repairs before selling?",
      answer: "Not at all. We buy properties as-is, which means you don't need to spend money on repairs, renovations, or improvements. We'll handle any necessary work after the purchase."
    },
    {
      question: "What documents do I need to provide?",
      answer: "We'll need basic property information including rent rolls, operating statements, property tax records, and deed information. Our team will guide you through exactly what's needed for your specific property."
    },
    {
      question: "Is the cash offer binding?",
      answer: "Our initial offer is subject to a brief property inspection and document review. Once we complete our due diligence, the offer becomes firm and we move forward to closing with guaranteed funds."
    },
    {
      question: "Do you buy properties nationwide?",
      answer: "Yes, we purchase multifamily properties in all 50 states. Our nationwide network of investors and local market expertise allows us to buy properties anywhere in the United States."
    },
    {
      question: "What happens to existing tenants?",
      answer: "We work with existing lease agreements and honor tenant rights. In most cases, tenants can remain in place during and after the sale. We'll discuss tenant situations during our property evaluation."
    }
  ];

  const toggleFAQ = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <section id="faq" className="py-20 bg-gray-50">
      <div className="max-w-4xl mx-auto px-6">
        <div className="text-center mb-16">
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
            Common Questions About <br />
            Selling Multifamily Properties
          </h2>
          <p className="text-xl text-gray-600">
            Get answers to frequently asked questions about our cash buying process
          </p>
        </div>

        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <div 
              key={index}
              className="bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow duration-200"
            >
              <button
                onClick={() => toggleFAQ(index)}
                className="w-full px-8 py-6 text-left flex items-center justify-between focus:outline-none"
              >
                <h3 className="text-lg font-semibold text-gray-900 pr-4">
                  {faq.question}
                </h3>
                <div className={`transform transition-transform duration-200 ${
                  openIndex === index ? 'rotate-45' : 'rotate-0'
                }`}>
                  <i className="ri-add-line text-orange-500 text-2xl"></i>
                </div>
              </button>
              
              {openIndex === index && (
                <div className="px-8 pb-6">
                  <div className="pt-2 border-t border-gray-100">
                    <p className="text-gray-600 leading-relaxed mt-4">
                      {faq.answer}
                    </p>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="text-center mt-12">
          <p className="text-gray-600 mb-6">
            Still have questions? We're here to help.
          </p>
          <a 
            href="#get-offer" 
            className="inline-flex items-center bg-orange-500 text-white px-8 py-4 rounded-lg font-semibold hover:bg-orange-600 transition-colors whitespace-nowrap cursor-pointer"
          >
            Get Your Questions Answered
            <i className="ri-arrow-right-line ml-2"></i>
          </a>
        </div>
      </div>

      {/* FAQ Schema Structured Data */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{
        __html: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": faqs.map(faq => ({
            "@type": "Question",
            "name": faq.question,
            "acceptedAnswer": {
              "@type": "Answer",
              "text": faq.answer
            }
          }))
        })
      }} />
    </section>
  );
};
